(function (global) {
  'use strict';

  const optionText = (value) => String(value?.name ?? value?.value ?? value ?? '');
  const same = (left, right) => optionText(left).trim().toLowerCase() === optionText(right).trim().toLowerCase();
  const valuesOf = (option) => (option?.values || []).map(optionText).filter(Boolean);

  function deriveOptionIndexes(config) {
    const options = config.options || [];
    const color = options.findIndex((option) => /^(color|colour)$/i.test(optionText(option.name).trim()));
    const size = options.findIndex((option) => /^size$/i.test(optionText(option.name).trim()));
    let pack = options.findIndex((option) => /^(pack|bundle|offer)$/i.test(optionText(option.name).trim()));
    if (pack < 0) {
      const packNames = (config.offers || []).map((offer) => offer.pack).filter(Boolean);
      pack = options.findIndex((option, index) => index !== color && index !== size &&
        valuesOf(option).some((value) => packNames.some((name) => same(name, value))));
    }
    if (color < 0 || size < 0 || pack < 0 || new Set([color, size, pack]).size !== 3) {
      throw new Error('The product options could not be loaded. Please refresh the page.');
    }
    return { color, size, pack };
  }

  function offerFor(config, quantity) {
    return (config.offers || []).find((offer) => Number(offer.quantity) === Number(quantity));
  }

  function findSingleVariant(config, row) {
    if (!row?.color || !row?.size) return undefined;
    const indexes = deriveOptionIndexes(config);
    const single = offerFor(config, 1);
    if (!single?.pack) return undefined;
    return (config.variants || []).find((variant) =>
      same(variant.options?.[indexes.pack], single.pack) &&
      same(variant.options?.[indexes.color], row.color) &&
      same(variant.options?.[indexes.size], row.size));
  }

  function buildCartItems(config, rows, quantity) {
    const expected = Number(config.variantCount ?? config.variants?.length ?? 0);
    const actual = new Set((config.variants || []).map((variant) => String(variant.id))).size;
    if (!Number.isInteger(expected) || expected < 1 || actual !== expected) {
      throw new Error('Some product options could not be loaded. Please refresh the page.');
    }
    const count = Number(quantity);
    if (![1, 3, 5].includes(count) || !offerFor(config, count)) {
      throw new Error('Please choose an available offer.');
    }
    if (!Array.isArray(rows) || rows.length !== count) {
      throw new Error('Please choose a color and size for every tank.');
    }
    const items = new Map();
    rows.forEach((row, index) => {
      if (!row?.color || !row?.size) {
        throw new Error(`Choose a color and size for tank ${index + 1}.`);
      }
      const variant = findSingleVariant(config, row);
      if (!variant?.available || !/^\d+$/.test(String(variant.id)) || Number(variant.id) <= 0) {
        throw new Error(`Tank ${index + 1} (${row.color}, ${row.size}) is unavailable. Please choose another color or size.`);
      }
      const key = String(variant.id);
      if (items.has(key)) items.get(key).quantity += 1;
      else items.set(key, { id: variant.id, quantity: 1 });
    });
    return [...items.values()];
  }

  function getOfferPrice(config, quantity, row) {
    const offer = offerFor(config, quantity);
    if (!offer) return null;
    const indexes = deriveOptionIndexes(config);
    const variants = (config.variants || []).filter((variant) => same(variant.options?.[indexes.pack], offer.pack));
    const matchingColor = variants.filter((variant) => row?.color && same(variant.options?.[indexes.color], row.color));
    const variant = matchingColor.find((candidate) => row?.size && same(candidate.options?.[indexes.size], row.size)) ||
      matchingColor[0] || variants[0];
    const price = variant?.price ?? offer.price;
    return Number.isFinite(Number(price)) && price !== null && price !== '' ? Number(price) : null;
  }

  function getOfferComparePrice(config, quantity, row) {
    const count = Number(quantity);
    const singlePrice = getOfferPrice(config, 1, row);
    return Number.isInteger(count) && count > 1 && offerFor(config, count) && singlePrice !== null
      ? singlePrice * count : null;
  }

  function normalizeRouteRoot(routeRoot) {
    const value = String(routeRoot || '/');
    if (!value.startsWith('/') || value.startsWith('//') || /[?#\\]/.test(value) ||
      value.split('/').some((part) => part === '.' || part === '..')) {
      throw new Error('The checkout address is not configured correctly.');
    }
    return value.endsWith('/') ? value : `${value}/`;
  }

  function storeAddress(storeUrl) {
    const url = new URL(storeUrl);
    if (!/^https?:$/.test(url.protocol) || url.username || url.password) {
      throw new Error('The store address is not configured correctly.');
    }
    return url;
  }

  function buildCartPermalink(storeUrl, routeRoot, items) {
    const store = storeAddress(storeUrl);
    if (!Array.isArray(items) || !items.length) throw new Error('Choose your tanks first.');
    const lines = items.map((item) => {
      if (!/^\d+$/.test(String(item.id)) || Number(item.id) <= 0 ||
        !Number.isInteger(item.quantity) || item.quantity < 1) throw new Error('Invalid checkout selection.');
      return `${item.id}:${item.quantity}`;
    });
    // Shopify cart permalinks open checkout by default; do not append ?storefront.
    return new URL(`${normalizeRouteRoot(routeRoot)}cart/${lines.join(',')}`, store.origin).href;
  }

  function buildVariantPageUrl(productUrl, sectionId, page, pageParam, storeUrl) {
    const store = storeAddress(storeUrl);
    const url = new URL(productUrl, store.origin);
    if (url.origin !== store.origin || !/^https?:$/.test(url.protocol)) throw new Error('The product address is not configured correctly.');
    if (!Number.isInteger(page) || page < 1 || !/^[a-zA-Z0-9_]+$/.test(pageParam || 'page')) throw new Error('Invalid product options page.');
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.delete('variant');
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set(pageParam || 'page', String(page));
    return url.href;
  }

  const helpers = { deriveOptionIndexes, findSingleVariant, buildCartItems, getOfferPrice, getOfferComparePrice, normalizeRouteRoot, buildCartPermalink, buildVariantPageUrl };
  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (!global?.customElements || !global.HTMLElement || global.customElements.get('lumive-tank-landing')) return;

  class LumiveTankLanding extends global.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-checkout-form]');
      this.button = this.querySelector('[data-checkout]');
      this.error = this.querySelector('[data-checkout-error]');
      this.status = this.querySelector('[data-selection-status]');
      this.rowsElement = this.querySelector('[data-tank-rows]');
      this.submitting = false;
      this.loading = false;
      this.listeners = new AbortController();
      try {
        this.config = JSON.parse(this.querySelector('[data-lumive-config]')?.textContent || '{}');
        this.indexes = deriveOptionIndexes(this.config);
        this.store = storeAddress(this.config.storeUrl || global.location.origin);
        this.routeRoot = normalizeRouteRoot(this.config.routeRoot || '/');
        this.preview = this.config.preview === true || global.location.origin !== this.store.origin;
        this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.config.currency || 'USD' });
        if (!this.form || !this.button || !this.rowsElement || !offerFor(this.config, 1)?.pack || !offerFor(this.config, 3)?.pack) {
          throw new Error('The offer could not be loaded. Please refresh the page.');
        }
        this.quantity = 3;
        const colors = valuesOf(this.config.options[this.indexes.color]);
        const availableColors = colors.filter((color) => this.colorAvailable(color));
        const initialColor = availableColors.find((color) => same(color, 'Beige')) || availableColors[0] || colors[0] || '';
        this.rows = Array.from({ length: 5 }, () => ({ color: initialColor, size: '' }));
        const { signal } = this.listeners;
        this.addEventListener('change', (event) => this.handleChange(event), { signal });
        this.form.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
        this.form.addEventListener('invalid', () => this.setError('Please choose a color and size for every tank.'), { signal, capture: true });
        this.renderRows();
        this.render();
        if (Number(this.config.variantCount || 0) > this.config.variants.length) this.loadVariants();
      } catch (error) {
        this.setError(error.message || 'The offer could not be loaded. Please refresh the page.');
        if (this.button) this.button.disabled = true;
      }
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.loadController?.abort();
      this.initialized = false;
    }

    colorAvailable(color) {
      const single = offerFor(this.config, 1);
      return (this.config.variants || []).some((variant) => variant.available &&
        same(variant.options?.[this.indexes.pack], single?.pack) &&
        same(variant.options?.[this.indexes.color], color));
    }

    handleChange(event) {
      const target = event.target;
      if (this.submitting || this.loading) return;
      if (target.matches('[data-offer-options] input[name="lumive-pack"]')) {
        const quantity = Number(target.value);
        if (![1, 3, 5].includes(quantity) || !offerFor(this.config, quantity)) return;
        this.quantity = quantity;
        this.renderRows();
      } else if (target.matches('[data-tank-color], [data-tank-size]')) {
        const index = Number(target.closest('[data-tank-row]')?.dataset.tankRow);
        if (!Number.isInteger(index) || !this.rows[index]) return;
        const changingColor = target.matches('[data-tank-color]');
        this.rows[index][changingColor ? 'color' : 'size'] = target.value;
        // Retain the explicitly selected size when changing color, even if unavailable.
        // Validation then asks the shopper to choose; it never substitutes another size.
        this.updateSizeOptions(index);
        if (changingColor) {
          this.dispatchEvent(new CustomEvent('lumive:color-change', { detail: { color: target.value } }));
        }
      } else return;
      this.setError('');
      this.render();
    }

    renderRows() {
      const fragment = document.createDocumentFragment();
      const colors = valuesOf(this.config.options[this.indexes.color]);
      const sizes = valuesOf(this.config.options[this.indexes.size]);
      this.rows.slice(0, this.quantity).forEach((row, index) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'lumive-tank-row';
        wrapper.dataset.tankRow = String(index);
        wrapper.setAttribute('role', 'group');
        wrapper.setAttribute('aria-label', `Tank ${index + 1} of ${this.quantity}`);
        const heading = document.createElement('div');
        heading.className = 'lumive-color-heading';
        const photo = document.createElement('img');
        photo.setAttribute('data-tank-preview', '');
        photo.width = 54;
        photo.height = 54;
        photo.loading = 'lazy';
        const title = document.createElement('div');
        const number = document.createElement('strong');
        number.textContent = `Tank ${index + 1}`;
        const colorName = document.createElement('span');
        colorName.setAttribute('data-current-color', '');
        title.append(number, colorName);
        heading.append(photo, title);
        const sizeLabel = document.createElement('label');
        sizeLabel.className = 'lumive-field lumive-size-field';
        const caption = document.createElement('span');
        caption.textContent = 'Size';
        const select = document.createElement('select');
        select.className = 'lumive-select';
        select.setAttribute('data-tank-size', '');
        select.setAttribute('aria-label', `Size for tank ${index + 1}`);
        select.required = true;
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select';
        select.append(placeholder);
        sizes.forEach(value => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.append(option);
        });
        select.value = row.size;
        sizeLabel.append(caption, select);
        const swatches = document.createElement('fieldset');
        swatches.className = 'lumive-color-options';
        const legend = document.createElement('legend');
        legend.className = 'lv-sr-only';
        legend.textContent = `Color for tank ${index + 1}`;
        swatches.append(legend);
        colors.forEach(color => {
          const media = this.colorMedia(color);
          const label = document.createElement('label');
          label.className = 'lumive-color-swatch';
          label.title = color;
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = `${this.id || 'lumive'}-color-${index}`;
          radio.value = color;
          radio.setAttribute('data-tank-color', '');
          radio.setAttribute('aria-label', `${color} for tank ${index + 1}`);
          radio.checked = same(row.color, color);
          radio.required = true;
          const chip = document.createElement('span');
          chip.className = 'lumive-swatch-chip';
          chip.setAttribute('aria-hidden', 'true');
          chip.style.backgroundColor = /^#[0-9a-f]{6}$/i.test(media?.hex || '') ? media.hex : '#ddd';
          label.append(radio, chip);
          swatches.append(label);
        });
        wrapper.append(heading, sizeLabel, swatches);
        fragment.append(wrapper);
      });
      this.rowsElement.replaceChildren(fragment);
      this.rows.slice(0, this.quantity).forEach((_, index) => this.updateSizeOptions(index));
    }

    colorMedia(color) {
      return (this.config.colorMedia || []).find(media => same(media.name, color));
    }

    updateSizeOptions(index) {
      const select = this.rowsElement.querySelector(`[data-tank-row="${index}"] [data-tank-size]`);
      if (!select) return;
      Array.from(select.options).forEach((option) => {
        if (!option.value) return;
        const available = findSingleVariant(this.config, { color: this.rows[index].color, size: option.value })?.available;
        option.disabled = !available;
        option.textContent = `${option.value}${available ? '' : ' — unavailable'}`;
      });
      select.value = this.rows[index].size;
    }

    render() {
      this.querySelectorAll('[data-offer-options] input[name="lumive-pack"]').forEach((input) => {
        input.checked = Number(input.value) === this.quantity;
        input.disabled = this.submitting || this.loading;
        const card = input.closest('[data-offer-card]');
        if (card) card.classList.toggle('is-selected', input.checked);
      });
      this.querySelectorAll('[data-offer-price]').forEach((element) => {
        const price = getOfferPrice(this.config, element.dataset.offerPrice, this.rows[0]);
        element.textContent = price === null ? '—' : this.money.format(price / 100);
      });
      this.querySelectorAll('[data-offer-compare]').forEach((element) => {
        const quantity = Number(element.dataset.offerCompare);
        const compare = getOfferComparePrice(this.config, quantity, this.rows[0]);
        const price = getOfferPrice(this.config, quantity, this.rows[0]);
        element.hidden = compare === null || price === null || compare <= price;
        element.textContent = compare === null ? '' : this.money.format(compare / 100);
      });
      const total = getOfferPrice(this.config, this.quantity, this.rows[0]);
      this.querySelectorAll('[data-checkout-total]').forEach((element) => {
        element.textContent = total === null ? '—' : this.money.format(total / 100);
      });
      this.button.disabled = this.submitting || this.loading;
      this.button.setAttribute('aria-busy', String(this.submitting || this.loading));
      this.querySelectorAll('[data-tank-size]').forEach(select => { select.disabled = this.submitting || this.loading; });
      this.querySelectorAll('[data-tank-color]').forEach(radio => {
        const index = Number(radio.closest('[data-tank-row]').dataset.tankRow);
        radio.checked = same(this.rows[index].color, radio.value);
        radio.disabled = this.submitting || this.loading || !this.colorAvailable(radio.value);
      });
      this.rows.slice(0, this.quantity).forEach((row, index) => {
        const wrapper = this.rowsElement.querySelector(`[data-tank-row="${index}"]`);
        wrapper.querySelector('[data-current-color]').textContent = `Color: ${row.color}`;
        const photo = wrapper.querySelector('[data-tank-preview]');
        const media = this.colorMedia(row.color);
        photo.hidden = !media?.image;
        if (media?.image) {
          photo.src = media.thumb || media.image;
          photo.alt = `${row.color} tank`;
        }
      });
      if (this.status) {
        const chosen = this.rows.slice(0, this.quantity).filter((row) => row.color && row.size).length;
        this.status.textContent = this.loading ? 'Loading all colors and sizes…' :
          this.submitting ? 'Preparing your secure checkout…' :
            `${this.quantity} ${this.quantity === 1 ? 'tank' : 'tanks'} selected. ${chosen} of ${this.quantity} sizes chosen.`;
      }
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async loadVariants() {
      this.loading = true;
      this.loadController?.abort();
      this.loadController = new AbortController();
      const { signal } = this.loadController;
      this.render();
      try {
        const first = await this.fetchVariantPage(1, 'page', signal);
        const pages = Number(first.pages);
        if (!Number.isInteger(pages) || pages < 1 || pages > 1000) throw new Error('Invalid product option pages.');
        const variants = new Map(first.variants.map((variant) => [String(variant.id), variant]));
        for (let page = 2; page <= pages; page += 3) {
          const tasks = [];
          for (let current = page; current <= Math.min(page + 2, pages); current += 1) {
            tasks.push(this.fetchVariantPage(current, first.page_param || 'page', signal));
          }
          for (const payload of await Promise.all(tasks)) {
            payload.variants.forEach((variant) => variants.set(String(variant.id), variant));
          }
        }
        if (variants.size !== Number(first.total) || variants.size !== Number(this.config.variantCount)) {
          throw new Error('Some product options could not be loaded. Please refresh the page.');
        }
        if (signal.aborted || !this.isConnected) return;
        this.config.variants = [...variants.values()];
        this.loading = false;
        this.renderRows();
        this.render();
      } catch (error) {
        if (signal.aborted || !this.isConnected) return;
        this.setError('Some colors and sizes could not load. Please refresh the page to try again.');
        // A partial catalog cannot produce a reliable selection; leave checkout disabled.
        this.loading = true;
        this.render();
        if (this.status) this.status.textContent = 'Product options are unavailable. Please refresh the page.';
      }
    }

    async fetchVariantPage(page, pageParam, signal) {
      const url = buildVariantPageUrl(this.config.productUrl, 'lumive-landing-variants', page, pageParam, this.store.href);
      const response = await fetch(url, { credentials: 'same-origin', signal });
      if (!response.ok) throw new Error('Product options request failed.');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = JSON.parse(document.querySelector('[data-lumive-variant-page]')?.textContent || 'null');
      if (!payload || String(payload.product_id) !== String(this.config.productId) || Number(payload.page) !== page || !Array.isArray(payload.variants)) {
        throw new Error('Invalid product options response.');
      }
      return payload;
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submitting || this.loading) return;
      this.setError('');
      let items;
      try {
        items = buildCartItems(this.config, this.rows.slice(0, this.quantity), this.quantity);
      } catch (error) {
        this.setError(error.message);
        const missingIndex = this.rows.slice(0, this.quantity).findIndex((row) => !row.size || !row.color);
        if (missingIndex >= 0) this.rowsElement.querySelector(`[data-tank-row="${missingIndex}"] [data-tank-size]`)?.focus();
        return;
      }
      this.submitting = true;
      this.render();
      let added = false;
      try {
        if (this.preview) {
          // An exported preview may run on localhost or file://. Never POST to its host.
          global.location.assign(buildCartPermalink(this.store.href, this.routeRoot, items));
          added = true; // Keep the button locked while the browser follows the permalink.
          return;
        }
        const response = await fetch(`${this.routeRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : 'These tanks could not be added. Please check your selections and try again.');
        }
        added = true;
        global.location.assign(`${this.routeRoot}checkout`);
      } catch (error) {
        if (!this.isConnected) return;
        this.setError(added ? 'Your tanks were added. Open your cart to continue to checkout.' :
          error.message || 'We could not prepare checkout. Please check your connection and try again.');
      } finally {
        if (!added && this.isConnected) {
          this.submitting = false;
          this.render();
        }
      }
    }
  }

  global.customElements.define('lumive-tank-landing', LumiveTankLanding);
})(typeof window === 'undefined' ? null : window);
